import {clsx} from 'clsx';
import React from 'react';
import GlobalHead from '../components/GlobalHead';
import LegacyFreeTemplate from '../templates/legacy/LegacyFreeTemplate';
import LegacyLandingTemplate from '../templates/legacy/LegacyLandingTemplate';
import LegacyTermsTemplate from '../templates/legacy/LegacyTermsTemplate';
import {useAdjustCreativeQuery} from '../hooks/use-adjust-creative-query';
import {useDownloadLink} from '../hooks/use-download-link';
import {useSiteMetadata} from '../hooks/use-site-metadata';
import {
  LEGACY_DESIGN_PAGES,
  HTML_CLASS_NAME_FOR_LEGACY_DESIGN_PAGES,
} from '../lib/constants';
import {adjustLinkPattern} from '../lib/adjust';
import type {LandingPageContext} from '../../gatsby-node';

import '../css/global.css';

/* eslint-disable react/no-multi-comp */

export type MarkdownQuery = {
  markdownRemark: {
    frontmatter: {
      description: string | null;
      layout: string;
      noIndex: boolean | null;
      ogImage: string | null;
      path: string;
      title: string;
      // layout が docs の場合にだけ使用される。
      // bgw: 背景色が白 | null: 背景色がグレー。
      type: 'bgw' | null;
    };
    html: string;
  };
};

/**
 * ensureMarkdownQuery
 * @param {Object} markdownRemark - LandingPageContext
 * @returns {MarkdownQuery}
 */
const ensureMarkdownQuery = (
  markdownRemark: LandingPageContext
): MarkdownQuery => {
  const errorMessage = `Unexpected query results: ${JSON.stringify(
    markdownRemark
  )}`;

  if (markdownRemark === null) {
    throw new Error(errorMessage);
  }

  const {frontmatter, html} = markdownRemark;

  if (
    !html ||
    !frontmatter?.layout ||
    !frontmatter?.path ||
    !frontmatter?.title
  ) {
    throw new Error(errorMessage);
  }

  const {description, layout, ogImage, path, title, type} = frontmatter;

  return {
    markdownRemark: {
      frontmatter: {
        description,
        layout,
        noIndex: frontmatter.noIndex || false,
        ogImage,
        path,
        title,
        // layout が docs の場合にだけ使用される。
        // bgw: 背景色が白 | null: 背景色がグレー。
        type: type === 'bgw' ? type : null,
      },
      html,
    },
  };
};

interface Props {
  location: Location;
  pageContext: LandingPageContext;
}

/**
 * PageTemplate
 * The template of pages defined by Markdown
 * @param {Object} props - Props
 * @returns {React.JSX.Element}
 */
export default function PageTemplate({location, pageContext}: Props) {
  const siteMetadata = useSiteMetadata();
  const {appStoreUrl, googlePlayUrl, unifiedStoreUrl} = useDownloadLink({
    pathname: location.pathname,
  });
  const {markdownRemark} = ensureMarkdownQuery(pageContext);
  const {frontmatter, html: rawHtml} = markdownRemark;
  const creativeQuery = useAdjustCreativeQuery({pathname: location.pathname});
  const html = rawHtml.replace(adjustLinkPattern, match =>
    match.includes('?')
      ? `${match}&${creativeQuery}`
      : `${match}?${creativeQuery}`
  );

  switch (frontmatter.layout) {
    case 'terms':
      return (
        <LegacyTermsTemplate
          {...markdownRemark}
          appStoreUrl={appStoreUrl}
          googlePlayUrl={googlePlayUrl}
          html={html}
          siteMetadata={siteMetadata}
          unifiedStoreUrl={unifiedStoreUrl}
        />
      );
    case 'landing':
      return (
        <LegacyLandingTemplate
          {...markdownRemark}
          appStoreUrl={appStoreUrl}
          googlePlayUrl={googlePlayUrl}
          html={html}
          siteMetadata={siteMetadata}
          unifiedStoreUrl={unifiedStoreUrl}
        />
      );
    case 'free':
      return (
        <LegacyFreeTemplate
          {...markdownRemark}
          appStoreUrl={appStoreUrl}
          googlePlayUrl={googlePlayUrl}
          html={html}
          siteMetadata={siteMetadata}
          unifiedStoreUrl={unifiedStoreUrl}
        />
      );
    case 'docs':
      return (
        <div className="Docs">
          {frontmatter.type !== 'bgw' && (
            <style>{`body { background-color: #f6f6fa }`}</style>
          )}
          <div
            className={clsx('Markdown', {
              'Markdown-bgw': frontmatter.type === 'bgw',
            })}
            dangerouslySetInnerHTML={{__html: html}}
          />
        </div>
      );
    default:
      return (
        <div>
          <div className="Markdown" dangerouslySetInnerHTML={{__html: html}} />
        </div>
      );
  }
}

/**
 * getClassName
 * @param {string} path current path
 * @return {string|null}
 */
function getClassName(path: string) {
  const className =
    LEGACY_DESIGN_PAGES.find(pagePath => path.startsWith(pagePath)) !==
    undefined
      ? HTML_CLASS_NAME_FOR_LEGACY_DESIGN_PAGES
      : '';

  /*
   * 「App Store からダウンロード」「Google Playで手に入れよう」ボタンが環境別に片方のみ表示されるようにする。
   * 非表示にする処理は以下の CSS ファイルで定義されている。
   * src/css/common/hideDownloadButtons.css
   *
   * refs: https://github.com/kanmu/vandle-lp/issues/715
   */
  if (typeof window === 'undefined') return className;
  const ua = navigator.userAgent.toLowerCase().trim();
  if (ua.includes('ipad') || ua.includes('iphone') || ua.includes('ipod')) {
    return `${className} is-ios`;
  } else if (ua.includes('android')) {
    return `${className} is-android`;
  }
  return className;
}

/**
 * Contents of the head element
 * @param {Object} pageContext - LandingPageContext
 * @returns {React.JSX.Element}
 */
export const Head = ({pageContext}: {pageContext: LandingPageContext}) => {
  const siteMetadata = useSiteMetadata();
  const {description, path, title, ogImage, layout, noIndex} =
    ensureMarkdownQuery(pageContext).markdownRemark.frontmatter;

  const pageTitle =
    layout === 'docs' ? `${title} | ${siteMetadata.siteTitle}` : title;
  const pageUrl = `${siteMetadata.siteUrl}${path}`;

  const additionalMetas: React.ComponentProps<typeof GlobalHead>['metas'] = [];

  if (noIndex) {
    additionalMetas.push({content: 'noindex', name: 'robots'});
  }

  return (
    <>
      <GlobalHead
        description={description}
        metas={additionalMetas}
        ogImagePath={ogImage}
        title={pageTitle}
        url={pageUrl}
      />
      <html className={getClassName(path)} />
    </>
  );
};
/* eslint-enable */
